import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "../context/AuthContext";
import { addressApi } from "../services/api";
import { addressIdEquals, getAddressId, isAddressDefault } from "../utils/address";

const AddressContext = createContext(null);
const SELECTED_ADDRESS_STORAGE_PREFIX = "shopper_selected_address:";

/**
 * Maps form-level camelCase fields to the API payload expected by the backend.
 * Backend service validates: fullName, phone, addressLine1, city, province, postalCode
 */
function toApiPayload(data) {
  return {
    fullName: (data.fullName || "").trim(),
    phone: (data.phone || "").trim(),
    addressLine1: (data.addressLine1 || "").trim(),
    addressLine2: (data.addressLine2 || "").trim(),
    landmark: (data.landmark || "").trim(),
    city: (data.city || "").trim(),
    province: (data.province || "").trim(),
    barangay: (data.barangay || "").trim(),
    postalCode: (data.postalCode || "").trim(),
    region: (data.region || "").trim(),
    label: (data.label || "").trim(),
    isDefault: Boolean(data.isDefault),
    latitude: data.latitude,
    longitude: data.longitude,
    pinConfirmed: data.pinConfirmed === true,
    locationSource: data.locationSource,
    locationQuality: data.locationQuality,
    geocodeProvider: data.geocodeProvider || null,
    providerPlaceId: data.providerPlaceId || null,
    accuracyM: data.accuracyM ?? null,
  };
}

function extractAddressErrorMessage(error, fallback) {
  const responseData = error?.response?.data;

  if (Array.isArray(responseData?.errors) && responseData.errors.length > 0) {
    return String(responseData.errors[0]);
  }

  if (typeof responseData?.msg === "string" && responseData.msg.trim()) {
    return responseData.msg.trim();
  }

  if (typeof responseData?.message === "string" && responseData.message.trim()) {
    return responseData.message.trim();
  }

  if (typeof error?.message === "string" && error.message.trim()) {
    return error.message
      .replace(/^Request failed:\s*\d+\s*[—-]\s*/i, "")
      .replace(/^Request failed:\s*/i, "")
      .trim();
  }

  return fallback;
}

function toAddressError(error, fallback) {
  const wrapped = new Error(extractAddressErrorMessage(error, fallback));
  wrapped.status = error?.status ?? error?.response?.status ?? null;
  wrapped.response = error?.response;
  wrapped.code = error?.code;
  return wrapped;
}

function normalizeAddressId(value) {
  if (value == null) return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;

  const normalized = String(value).trim();
  if (!normalized) return null;

  const parsed = Number(normalized);
  if (Number.isSafeInteger(parsed) && String(parsed) === normalized) {
    return parsed;
  }

  return normalized;
}

function addressMatchesId(address, id) {
  return addressIdEquals(getAddressId(address), id);
}

function resolveSelectedAddressId(addresses, preferredId) {
  const list = Array.isArray(addresses) ? addresses : [];
  if (list.length === 0) return null;

  const normalizedPreferredId = normalizeAddressId(preferredId);
  if (
    normalizedPreferredId != null &&
    list.some((address) => addressMatchesId(address, normalizedPreferredId))
  ) {
    return normalizedPreferredId;
  }

  const defaultAddress = list.find(isAddressDefault);
  return normalizeAddressId(defaultAddress?.id ?? list[0]?.id ?? null);
}

function getSelectedAddressStorageKey(userId, token) {
  if (userId != null && userId !== "") {
    return `${SELECTED_ADDRESS_STORAGE_PREFIX}user:${userId}`;
  }

  if (token) {
    return `${SELECTED_ADDRESS_STORAGE_PREFIX}token:${String(token).slice(-24)}`;
  }

  return `${SELECTED_ADDRESS_STORAGE_PREFIX}shopper`;
}

function readSelectedAddressId(storageKey) {
  if (!storageKey) return null;

  try {
    return normalizeAddressId(window.localStorage.getItem(storageKey));
  } catch {
    return null;
  }
}

function writeSelectedAddressId(storageKey, id) {
  if (!storageKey) return;

  try {
    if (id == null) {
      window.localStorage.removeItem(storageKey);
      return;
    }

    window.localStorage.setItem(storageKey, String(id));
  } catch {
    // Ignore localStorage failures and keep the in-memory selection.
  }
}

function findCreatedAddress(addresses, data) {
  return addresses.find(
    (address) =>
      String(address.full_name || "") === String((data.fullName || "").trim()) &&
      String(address.phone || "") === String((data.phone || "").trim()) &&
      String(address.address_line1 || "") === String((data.addressLine1 || "").trim()) &&
      String(address.postal_code || "") === String((data.postalCode || "").trim())
  );
}

/**
 * AddressProvider + useAddress – shared shopper address state.
 *
 * Exposes:
 *  addresses       – full list from API
 *  selectedId      – currently selected address ID
 *  setSelectedId   – manually override selection
 *  selectedAddress – the address object matching selectedId
 *  loading         – fetch or mutation refresh in flight
 *  error           – last error string or null
 *  load(options)   – (re)load all addresses with deterministic selection fallback
 *  createAddr(data, options)      – POST new address
 *  updateAddr(id, data)  – PUT existing address
 *  deleteAddr(id)        – DELETE address
 *  setDefault(id)        – PATCH default address
 */
export function AddressProvider({ children }) {
  const { isAuthenticated, token, user } = useAuth();

  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIdState, setSelectedIdState] = useState(null);

  const selectedIdRef = useRef(null);
  const loadSequenceRef = useRef(0);

  const storageKey = useMemo(
    () =>
      isAuthenticated ? getSelectedAddressStorageKey(user?.id ?? null, token) : null,
    [isAuthenticated, token, user?.id]
  );

  const setSelectedId = useCallback((valueOrUpdater) => {
    setSelectedIdState((previous) => {
      const nextValue =
        typeof valueOrUpdater === "function" ? valueOrUpdater(previous) : valueOrUpdater;
      const normalized = normalizeAddressId(nextValue);
      selectedIdRef.current = normalized;
      return normalized;
    });
  }, []);

  useEffect(() => {
    selectedIdRef.current = selectedIdState;
  }, [selectedIdState]);

  useEffect(() => {
    if (!storageKey) {
      setAddresses([]);
      setError(null);
      setLoading(false);
      setSelectedId(null);
      return;
    }

    setAddresses([]);
    setError(null);
    setLoading(false);
    setSelectedId(readSelectedAddressId(storageKey));
  }, [storageKey, setSelectedId]);

  useEffect(() => {
    if (!storageKey) return;
    writeSelectedAddressId(storageKey, selectedIdState);
  }, [selectedIdState, storageKey]);

  useEffect(() => {
    if (typeof window === "undefined" || !storageKey) return undefined;

    const handleStorage = (event) => {
      if (event.key !== storageKey) return;
      setSelectedId(readSelectedAddressId(storageKey));
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [setSelectedId, storageKey]);

  const load = useCallback(
    async (options = {}) => {
      if (!isAuthenticated) {
        setAddresses([]);
        setError(null);
        setLoading(false);
        setSelectedId(null);
        return [];
      }

      const requestId = loadSequenceRef.current + 1;
      loadSequenceRef.current = requestId;

      setLoading(true);
      setError(null);

      try {
        const list = await addressApi.fetchAddresses();
        const arr = Array.isArray(list) ? list : [];

        if (requestId !== loadSequenceRef.current) {
          return arr;
        }

        setAddresses(arr);
        setSelectedId(resolveSelectedAddressId(arr, options.preferredId ?? selectedIdRef.current));
        return arr;
      } catch (err) {
        const friendlyError = toAddressError(err, "Failed to load addresses. Please try again.");
        if (requestId === loadSequenceRef.current) {
          setError(friendlyError.message);
        }
        throw friendlyError;
      } finally {
        if (requestId === loadSequenceRef.current) {
          setLoading(false);
        }
      }
    },
    [isAuthenticated, setSelectedId]
  );

  const createAddr = useCallback(
    async (data, options = {}) => {
      setError(null);

      try {
        const created = await addressApi.createAddress(toApiPayload(data));
        const createdId = normalizeAddressId(getAddressId(created));
        const shouldSelectCreated = options.selectCreated !== false;
        const preferredIdForReload = shouldSelectCreated
          ? createdId ?? selectedIdRef.current
          : data.isDefault
            ? createdId ?? null
            : selectedIdRef.current;
        const refreshed = await load({ preferredId: preferredIdForReload });

        if (shouldSelectCreated && createdId != null) {
          setSelectedId(createdId);
          return created;
        }

        if (!shouldSelectCreated) {
          return created;
        }

        const createdFromFields = findCreatedAddress(refreshed, data);
        if (createdFromFields) {
          setSelectedId(createdFromFields.id);
          return created;
        }

        if (data.isDefault || refreshed.length === 1) {
          setSelectedId(resolveSelectedAddressId(refreshed, null));
        }

        return created;
      } catch (err) {
        const friendlyError = toAddressError(err, "Failed to save address. Please try again.");
        setError(friendlyError.message);
        throw friendlyError;
      }
    },
    [load, setSelectedId]
  );

  const updateAddr = useCallback(
    async (id, data) => {
      setError(null);

      try {
        const targetId = normalizeAddressId(id);
        const updated = await addressApi.updateAddress(targetId, toApiPayload(data));
        const shouldSelectUpdated =
          String(selectedIdRef.current) === String(targetId) ||
          Boolean(data?.isDefault) ||
          isAddressDefault(updated);

        await load({
          preferredId: shouldSelectUpdated ? targetId : selectedIdRef.current,
        });

        if (shouldSelectUpdated) {
          setSelectedId(targetId);
        }

        return updated;
      } catch (err) {
        const friendlyError = toAddressError(err, "Failed to update address. Please try again.");
        setError(friendlyError.message);
        throw friendlyError;
      }
    },
    [load, setSelectedId]
  );

  const deleteAddr = useCallback(
    async (id) => {
      setError(null);

      try {
        await addressApi.deleteAddress(id);
        const preferredId =
          String(selectedIdRef.current) === String(id) ? null : selectedIdRef.current;
        return await load({ preferredId });
      } catch (err) {
        const friendlyError = toAddressError(err, "Failed to delete address. Please try again.");
        setError(friendlyError.message);
        throw friendlyError;
      }
    },
    [load]
  );

  const setDefault = useCallback(
    async (id) => {
      setError(null);

      try {
        await addressApi.setDefaultAddress(id);
        await load({ preferredId: id });
        setSelectedId(id);
      } catch (err) {
        const friendlyError = toAddressError(
          err,
          "Failed to set default address. Please try again."
        );
        setError(friendlyError.message);
        throw friendlyError;
      }
    },
    [load, setSelectedId]
  );

  const selectedAddress = useMemo(
    () => addresses.find((address) => addressMatchesId(address, selectedIdState)) ?? null,
    [addresses, selectedIdState]
  );

  const value = useMemo(
    () => ({
      addresses,
      loading,
      error,
      selectedId: selectedIdState,
      setSelectedId,
      selectedAddress,
      load,
      createAddr,
      updateAddr,
      deleteAddr,
      setDefault,
    }),
    [
      addresses,
      loading,
      error,
      selectedIdState,
      setSelectedId,
      selectedAddress,
      load,
      createAddr,
      updateAddr,
      deleteAddr,
      setDefault,
    ]
  );

  return <AddressContext.Provider value={value}>{children}</AddressContext.Provider>;
}

export function useAddress() {
  const context = useContext(AddressContext);
  if (!context) {
    throw new Error("useAddress must be used inside AddressProvider");
  }

  return context;
}
